import React from "react";
import { ServerPaginatedTable } from "../../../shared";
import { Chip, Stack, Button } from "@mui/material";
import { Link, useParams, useNavigate } from "react-router-dom";
import InputControl from "../../../shared/components/InputControl";
import { useCustomerStampCards } from "../hooks/useCustomerStampCards";
import CloseIcon from "@mui/icons-material/Close";

export const CustomersStampCards = () => {
  const { id: customerId } = useParams();

  const {
    state,
    statusOptions,
    gotoPage,
    handleSort,
    setPageSize,
    handleTableSearch,
    handleStatusFilter,
    handleClearFilters,
  } = useCustomerStampCards({ customerId: customerId });

  const ActionButtons = ({ row }) => {
    return (
      <div className="action-buttons">
        <Link to={`/customers/stamp-card/${customerId}/${row.id}`}>
          <img alt="details" src="/icons/arrow_forward.svg" />
        </Link>
      </div>
    );
  };

  const getStatusStyle = (statusValue) => {
    const styles = {
      1: { backgroundColor: "#FFF4CE", color: "#C07302" }, // Ongoing
      2: { backgroundColor: "#FFE7E3", color: "#C42B1C" }, // Expired
      3: { backgroundColor: "#0000000F", color: "#A3A3A3" }, // Inactive
      4: { backgroundColor: "#E1FBF2", color: "#0F875B" }, // Completed
    };
    return (
      styles[statusValue] || {
        backgroundColor: "#eee",
        color: "#666",
      }
    );
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Stamp Card Name",
        accessor: "stamp_card_name",
      },
      {
        Header: "Cycle",
        accessor: "cycle",
        align: "center",
        Cell: ({ value }) => value || "--",
      },
      {
        Header: "Stamps",
        accessor: "stamps_earned",
        align: "center",
        Cell: ({ row }) => {
          return (
            <Chip
              label={`${row.original.stamps_earned || 0}/${row.original.total_stamps || 0}`}
              color="primary"
              size="small"
              sx={{
                bgcolor: "#001A52",
                "& .MuiChip-label": {},
              }}
              disabled={false}
            />
          );
        },
      },
      {
        Header: "Started On",
        accessor: "started_on",
        Cell: ({ value }) => value || "--",
      },
      {
        Header: "End Date",
        accessor: "ended_on",
        Cell: ({ value }) => value || "--",
      },
      {
        Header: "Status",
        accessor: "status_label",
        Cell: ({ value, row }) => {
          const statusStyle = getStatusStyle(row.original.status);
          return (
            <Chip
              label={value || "Unknown"}
              variant="filled"
              size="small"
              sx={{
                color: statusStyle.color,
                backgroundColor: statusStyle.backgroundColor,
              }}
            />
          );
        },
      },
      {
        Header: "Action",
        id: "action",
        align: "right",
        disableSortBy: true,
        accessor: (row, index) => {
          return <ActionButtons key={index} row={row} />;
        },
      },
    ],
    [],
  );

  return (
    <ServerPaginatedTable
      columns={columns}
      data={state.data}
      isLoading={state.isLoading}
      gotoPage={gotoPage}
      searchBar={true}
      defaultSearch={false}
      handleSort={handleSort}
      setPageSize={setPageSize}
      pagination={state.pagination}
      sortByGlobal={state.sortBy}
      filterContent={
        <InputControl
          type="table-search"
          name="search"
          value={state.searchText}
          onChange={handleTableSearch}
          onClear={() => handleTableSearch({ target: { value: "" } })}
        />
      }
      filterSection={
        <Stack
          direction="row"
          gap="16px"
          alignItems="center"
          justifyContent={"space-between"}
          width="100%"
        >
          <InputControl
            type="select"
            name="status"
            label="Status"
            width="150px"
            value={state.filters.status}
            onChange={handleStatusFilter}
            optionValue={statusOptions}
            clearable
          />
          <Button
            type="button"
            size="medium"
            variant="text"
            color="gray"
            sx={{
              minWidth: "40px",
              p: "8px",
            }}
            onClick={handleClearFilters}
            disabled={!state.filters.status}
          >
            <CloseIcon />
          </Button>
        </Stack>
      }
      onRowLink={(row) => `/customers/stamp-card/${customerId}/${row.id}`}
    />
  );
};
